/**
 * Centralized Constants
 * All magic strings, storage keys, and configuration values
 */

// ============================================================================
// STORAGE KEYS
// ============================================================================

export const STORAGE_KEYS = {
  // Token storage (used by API client). ACCESS_TOKEN / REFRESH_TOKEN /
  // TOKEN_EXPIRES_AT are legacy keys: the access token now lives in memory and
  // the refresh token in an HttpOnly cookie. They are still cleared on logout
  // to purge any tokens persisted by an older build.
  ACCESS_TOKEN: "access_token",
  REFRESH_TOKEN: "refresh_token",
  TOKEN_EXPIRES_AT: "token_expires_at",
  REMEMBER_ME: "ilmiq_remember_me",
  // Legacy per-tab sessionStorage marker for "remember me" unticked. The
  // server now sets a browser-session cookie instead, which every tab shares;
  // the key is only still cleared on logout.
  SESSION_ACTIVE: "ilmiq_session_active",
  // When anyone last used the app in this browser (any tab). Drives the idle
  // sign-out for "remember me" unticked logins.
  LAST_ACTIVITY: "ilmiq_last_activity",
  // "idle:<epoch ms>" after an idle sign-out, so the login page can say why.
  SIGNOUT_REASON: "ilmiq_signout_reason",

  // Zustand store persistence
  AUTH_STORAGE: "auth-storage",
  UI_STORAGE: "ui-storage",

  // Auth synchronization across tabs
  AUTH_SYNC: "ilmiq_auth_sync",
  AUTH_LOGOUT: "ilmiq_auth_logout",
  AUTH_LOGIN: "ilmiq_auth_login",
  AUTH_REFRESH: "ilmiq_auth_refresh",

  // Form auto-save prefix
  AUTOSAVE_PREFIX: "ilmiq_autosave_",
} as const;

// ============================================================================
// USER ROLES
// ============================================================================

export const USER_ROLES = {
  SUPER_USER: "super_user",
  ADMIN: "admin",
  PRINCIPAL: "principal",
  STAFF: "staff",
  TEACHER: "teacher",
  PARENT: "parent",
} as const;

export type UserRoleKey = keyof typeof USER_ROLES;
export type UserRoleValue = (typeof USER_ROLES)[UserRoleKey];

// ============================================================================
// PERMISSIONS
// ============================================================================
//
// The one place the UI decides who may see a page or an action. Routes
// (ProtectedRoute permission=...), the sidebar, keyboard chords and buttons
// (useCan) all read ROLE_PERMISSIONS through authStore.hasPermission, and
// super_user holds the wildcard because commons.rbac.check_permission lets
// super_user through. Exception: an endpoint that checks a role set directly
// instead of check_permission refuses super_user — today the placement-rubric
// PUT (enrollments ADMIN_LIKE_ROLES), which ClassDetailPage hides from
// super_user by hand.
//
// Two kinds of key:
// - Page keys (manage:classes, manage:fees, ...) open a section of the app.
//   They are deliberately broader than any one endpoint: a teacher holds
//   manage:classes and manage:students so they can open a class or student
//   page linked from their portal (rosters, gradebooks), even though they may
//   not edit either.
// - Action keys (void:invoices, manage:class-setup, ...) mirror ONE
//   endpoint's check_permission list, cited beside each. Gate a button on
//   these, never on a page key, or the button outlives the server's "no".

export const PERMISSIONS = {
  // Wildcard
  ALL: "*",

  // Dashboard
  VIEW_DASHBOARD: "view:dashboard",

  // Users
  VIEW_USERS: "view:users",
  MANAGE_USERS: "manage:users",

  // Students
  VIEW_STUDENTS: "view:students",
  MANAGE_STUDENTS: "manage:students",

  // Families
  VIEW_FAMILIES: "view:families",
  MANAGE_FAMILIES: "manage:families",

  // Applications
  MANAGE_APPLICATIONS: "manage:applications",
  APPROVE_APPLICATIONS: "approve:applications",

  // Classes
  VIEW_CLASSES: "view:classes",
  MANAGE_CLASSES: "manage:classes",

  // Campuses
  VIEW_CAMPUSES: "view:campuses",
  MANAGE_CAMPUSES: "manage:campuses",

  // Fees & Billing
  MANAGE_FEES: "manage:fees",
  MANAGE_PAYMENTS: "manage:payments",
  MANAGE_DISCOUNTS: "manage:discounts",

  // Teachers
  MANAGE_TEACHERS: "manage:teachers",

  // Enrollments
  VIEW_ENROLLMENTS: "view:enrollments",
  MANAGE_ENROLLMENTS: "manage:enrollments",
  EXPORT_ENROLLMENTS: "export:enrollments",

  // Reports
  VIEW_REPORTS: "view:reports",

  // Settings
  MANAGE_SETTINGS: "manage:settings",

  // Parent-specific
  VIEW_OWN_FAMILY: "view:own:family",
  VIEW_OWN_STUDENTS: "view:own:students",
  VIEW_OWN_INVOICES: "view:own:invoices",
  VIEW_OWN_PAYMENTS: "view:own:payments",
  VIEW_OWN_STATEMENTS: "view:own:statements",
  MAKE_PAYMENTS: "make:payments",
  MANAGE_OWN_PAYMENT_METHODS: "manage:own:payment-methods",

  // Action keys — each mirrors one endpoint's check_permission list.
  /** invoices: POST /void — ["admin"] */
  VOID_INVOICES: "void:invoices",
  /** invoices: credit memos, invoice discounts, remove late fee — ["admin"] */
  MANAGE_CREDIT_MEMOS: "manage:credit-memos",
  /** invoices: POST /<id>/resend-email — ["admin", "principal", "staff"] */
  RESEND_INVOICES: "resend:invoices",
  /** payments: POST /<id>/refund — ["admin"] */
  REFUND_PAYMENTS: "refund:payments",
  /** discounts: POST/DELETE /zakat/transfers — ["admin"] */
  MANAGE_ZAKAT_TRANSFERS: "manage:zakat-transfers",
  /** curriculum: subjects, grade levels, units, lessons, resources — CURRICULUM_MANAGE_PERMISSIONS */
  MANAGE_CURRICULUM: "manage:curriculum",
  /** curriculum: generate / revoke report cards — CURRICULUM_MANAGE_PERMISSIONS */
  MANAGE_REPORT_CARDS: "manage:report-cards",
  /**
   * classes: edit, clone, teachers, student groups (PUT /<id>/student-teachers),
   * subjects, grade levels — ["admin", "principal", "staff"];
   * attendance: POST /classes/<id>/sessions/generate
   * (same three); enrollments placement rubric — ADMIN_LIKE_ROLES (same
   * three, but no super_user: see ClassDetailPage).
   */
  MANAGE_CLASS_SETUP: "manage:class-setup",
  /** classes: POST/DELETE /<id>/fees — ["admin"] */
  MANAGE_CLASS_FEES: "manage:class-fees",
  /** classes: DELETE /<id> (archive class) — ["admin"] */
  DELETE_CLASSES: "delete:classes",
  /**
   * Read fee setup: classes GET /<id>/fees and fees GET / (fee types) —
   * admin, principal, staff (parents read their own class fees elsewhere).
   */
  VIEW_FEES: "view:fees",
  /** hifdh: POST /summaries/<id>/sign — PRINCIPAL_PERMISSIONS ["admin", "principal"] */
  SIGN_HIFDH_PRINCIPAL: "sign:hifdh-principal",
  /** admin: GET /audit-log — admin, principal */
  VIEW_AUDIT_LOG: "view:audit-log",
  /** admin: GET /dashboard — ["admin", "principal"] (financial metrics) */
  VIEW_SCHOOL_METRICS: "view:school-metrics",
  /** users: GET / with role=teacher — admin, principal, staff (class teacher picker) */
  LIST_TEACHERS: "list:teachers",
} as const;

export type PermissionKey = keyof typeof PERMISSIONS;
export type PermissionValue = (typeof PERMISSIONS)[PermissionKey];

// Role to permissions mapping
export const ROLE_PERMISSIONS: Record<UserRoleValue, PermissionValue[]> = {
  [USER_ROLES.SUPER_USER]: [PERMISSIONS.ALL],
  [USER_ROLES.ADMIN]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.MANAGE_TEACHERS,
    PERMISSIONS.MANAGE_STUDENTS,
    PERMISSIONS.MANAGE_FAMILIES,
    PERMISSIONS.MANAGE_APPLICATIONS,
    PERMISSIONS.MANAGE_CLASSES,
    PERMISSIONS.MANAGE_CAMPUSES,
    PERMISSIONS.MANAGE_FEES,
    PERMISSIONS.MANAGE_PAYMENTS,
    PERMISSIONS.MANAGE_DISCOUNTS,
    PERMISSIONS.MANAGE_ENROLLMENTS,
    PERMISSIONS.VIEW_ENROLLMENTS,
    PERMISSIONS.EXPORT_ENROLLMENTS,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.MANAGE_SETTINGS,
    PERMISSIONS.VOID_INVOICES,
    PERMISSIONS.MANAGE_CREDIT_MEMOS,
    PERMISSIONS.REFUND_PAYMENTS,
    PERMISSIONS.MANAGE_ZAKAT_TRANSFERS,
    PERMISSIONS.MANAGE_CURRICULUM,
    PERMISSIONS.MANAGE_REPORT_CARDS,
    PERMISSIONS.MANAGE_CLASS_SETUP,
    PERMISSIONS.MANAGE_CLASS_FEES,
    PERMISSIONS.DELETE_CLASSES,
    PERMISSIONS.SIGN_HIFDH_PRINCIPAL,
    PERMISSIONS.VIEW_AUDIT_LOG,
    PERMISSIONS.VIEW_SCHOOL_METRICS,
    PERMISSIONS.LIST_TEACHERS,
    PERMISSIONS.RESEND_INVOICES,
    PERMISSIONS.VIEW_FEES,
  ],
  [USER_ROLES.PRINCIPAL]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.MANAGE_TEACHERS,
    PERMISSIONS.MANAGE_STUDENTS,
    PERMISSIONS.MANAGE_FAMILIES,
    PERMISSIONS.MANAGE_APPLICATIONS,
    PERMISSIONS.MANAGE_CLASSES,
    PERMISSIONS.MANAGE_CAMPUSES,
    PERMISSIONS.MANAGE_FEES,
    PERMISSIONS.MANAGE_ENROLLMENTS,
    PERMISSIONS.VIEW_ENROLLMENTS,
    PERMISSIONS.EXPORT_ENROLLMENTS,
    PERMISSIONS.APPROVE_APPLICATIONS,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.MANAGE_CURRICULUM,
    PERMISSIONS.MANAGE_REPORT_CARDS,
    PERMISSIONS.MANAGE_CLASS_SETUP,
    PERMISSIONS.SIGN_HIFDH_PRINCIPAL,
    PERMISSIONS.VIEW_AUDIT_LOG,
    PERMISSIONS.VIEW_SCHOOL_METRICS,
    PERMISSIONS.LIST_TEACHERS,
    PERMISSIONS.RESEND_INVOICES,
    PERMISSIONS.VIEW_FEES,
  ],
  [USER_ROLES.STAFF]: [
    PERMISSIONS.VIEW_DASHBOARD,
    // No MANAGE_TEACHERS: the users API refuses staff on every Teachers-page call.
    PERMISSIONS.MANAGE_STUDENTS,
    PERMISSIONS.MANAGE_FAMILIES,
    PERMISSIONS.MANAGE_APPLICATIONS,
    PERMISSIONS.MANAGE_CLASSES,
    PERMISSIONS.MANAGE_CAMPUSES,
    PERMISSIONS.MANAGE_FEES,
    PERMISSIONS.MANAGE_ENROLLMENTS,
    PERMISSIONS.VIEW_ENROLLMENTS,
    PERMISSIONS.EXPORT_ENROLLMENTS,
    PERMISSIONS.MANAGE_CLASS_SETUP,
    PERMISSIONS.LIST_TEACHERS,
    PERMISSIONS.RESEND_INVOICES,
    PERMISSIONS.VIEW_FEES,
  ],
  [USER_ROLES.TEACHER]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_STUDENTS,
    PERMISSIONS.MANAGE_STUDENTS,
    PERMISSIONS.VIEW_CLASSES,
    PERMISSIONS.MANAGE_CLASSES,
    PERMISSIONS.VIEW_CAMPUSES,
    PERMISSIONS.VIEW_ENROLLMENTS,
  ],
  [USER_ROLES.PARENT]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_OWN_FAMILY,
    PERMISSIONS.VIEW_OWN_STUDENTS,
    PERMISSIONS.VIEW_ENROLLMENTS,
    PERMISSIONS.VIEW_OWN_INVOICES,
    PERMISSIONS.VIEW_OWN_PAYMENTS,
    PERMISSIONS.MAKE_PAYMENTS,
    PERMISSIONS.VIEW_OWN_STATEMENTS,
    PERMISSIONS.MANAGE_OWN_PAYMENT_METHODS,
  ],
};

// ============================================================================
// API CONFIGURATION
// ============================================================================

export const API_CONFIG = {
  TIMEOUT_MS: 30000,
  REFRESH_TIMEOUT_MS: 10000,
  QUEUE_TIMEOUT_MS: 15000,
  MAX_REFRESH_ATTEMPTS: 3,
} as const;

// "Remember me" unticked: every tab signs out after this long without
// activity in any of them, after a warning. The server ends such a login
// after 24 h without a refresh as a backstop (JWT_SESSION_REFRESH_TOKEN_EXPIRY).
export const AUTH_CONFIG = {
  IDLE_TIMEOUT_MS: 4 * 60 * 60 * 1000,
  IDLE_WARNING_MS: 2 * 60 * 1000,
  // Activity is written to localStorage at most this often per tab.
  ACTIVITY_WRITE_THROTTLE_MS: 15 * 1000,
  // While the warning shows, the countdown ticks every second; otherwise a
  // tab only wakes at its next deadline, and at least this often (another
  // tab's activity moves the deadline). Ticked logins never wake.
  IDLE_CHECK_INTERVAL_MS: 1000,
  IDLE_MAX_SLEEP_MS: 60 * 1000,
} as const;

// ============================================================================
// UI CONFIGURATION
// ============================================================================

export const UI_CONFIG = {
  // Layout
  SIDEBAR_WIDTH: 280,
  HEADER_HEIGHT: 64,
  CONTENT_MAX_WIDTH: 1400,

  // Pagination
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100] as const,

  // Debounce delays (ms)
  SEARCH_DEBOUNCE_MS: 400,
  AUTOSAVE_DEBOUNCE_MS: 1000,
  URL_FILTER_DEBOUNCE_MS: 300,

  // Auto-save expiration (ms)
  AUTOSAVE_EXPIRATION_MS: 24 * 60 * 60 * 1000, // 24 hours
} as const;

// ============================================================================
// STATUS VALUES
// ============================================================================

export const ENTITY_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
  WITHDRAWN: "withdrawn",
  PAUSED: "paused",
} as const;

export const INVOICE_STATUS = {
  DRAFT: "draft",
  PENDING: "pending",
  PAID: "paid",
  PARTIAL: "partial",
  OVERDUE: "overdue",
  CANCELLED: "cancelled",
  REFUNDED: "refunded",
} as const;

export const PAYMENT_STATUS = {
  PENDING: "pending",
  COMPLETED: "completed",
  FAILED: "failed",
  REFUNDED: "refunded",
} as const;

// ============================================================================
// ROUTES
// ============================================================================

export const ROUTES = {
  // Auth
  LOGIN: "/login",
  FORGOT_PASSWORD: "/forgot-password",
  RESET_PASSWORD: "/reset-password",

  // Dashboard
  DASHBOARD: "/dashboard",

  // Admin
  STUDENTS: "/students",
  FAMILIES: "/families",
  CLASSES: "/classes",
  CAMPUSES: "/campuses",
  ENROLLMENTS: "/enrollments",
  INVOICES: "/invoices",
  PAYMENTS: "/payments",
  FEES: "/fees",
  DISCOUNTS: "/discounts",
  REPORTS: "/reports",
  SETTINGS: "/settings",
  USERS: "/users",

  // Parent portal
  PARENT_DASHBOARD: "/parent/dashboard",
  PARENT_STUDENTS: "/parent/students",
  PARENT_INVOICES: "/parent/invoices",
  PARENT_PAYMENTS: "/parent/payments",
  PARENT_PROFILE: "/parent/profile",
} as const;
